import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert,
  TableContainer, Table, TableHead, TableRow, TableCell,
  TableBody, Paper, Checkbox, Select, MenuItem, FormControl,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  LinearProgress, Chip
} from '@mui/material';
import { Search as SearchIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { format } from 'date-fns';

const TestCasesCleanup = ({ selectedProject, authenticatedUser, apiClient }) => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [error, setError] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState('');

  const handleAnalyzeTestCases = useCallback(async () => {
    // Validate project identifier before making API call
    console.log('TestCasesCleanup: handleAnalyzeTestCases called');
    console.log('TestCasesCleanup: selectedProject:', selectedProject);
    
    const projectIdentifier = selectedProject?.id || selectedProject?.project_id;
    console.log('TestCasesCleanup: projectIdentifier:', projectIdentifier);
    
    if (!projectIdentifier) {
      console.error('TestCasesCleanup: No project identifier found!');
      setError('No project selected. Please select a project first.');
      setAnalyzing(false);
      setLoading(false);
      return;
    }
    
    setAnalyzing(true);
    setLoading(true);
    setError(null);
    setResults([]);
    setProgress(0);

    let currentProgress = 0;
    const progressInterval = setInterval(() => {
      currentProgress = Math.min(currentProgress + 5, 95);
      setProgress(currentProgress);
    }, 500);

    try {
      console.log('TestCasesCleanup: Making API call with project_id:', projectIdentifier);
      
      const response = await apiClient.makeRequest('/health-insights/test-cases/analyze', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectIdentifier,
        }),
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (response.success) {
        setResults(response.data.testCases || []);
        setSelectedIds(new Set());
      } else {
        setError(response.message || 'Failed to analyze test cases.');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to analyze test cases. Please try again.');
    } finally {
      setAnalyzing(false);
      setLoading(false);
    }
  }, [selectedProject, apiClient]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = new Set(results.map((tc) => tc.id));
      setSelectedIds(newSelecteds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleClick = (event, id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const isSelected = (id) => selectedIds.has(id);

  const handleStatusChange = async (event, testCaseId) => {
    const newStatus = event.target.value;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.makeRequest('/health-insights/test-cases/update-status', {
        method: 'POST',
        body: JSON.stringify({
          project_id: selectedProject?.id || selectedProject?.project_id,
          test_case_id: testCaseId,
          status: newStatus,
        }),
      });

      if (response.success) {
        setResults((prev) =>
          prev.map((tc) => (tc.id === testCaseId ? { ...tc, status: newStatus, updated: true } : tc))
        );
      } else {
        setError(response.message || 'Failed to update test case status.');
      }
    } catch (err) {
      console.error('Status update error:', err);
      setError(err.message || 'Failed to update test case status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = (action) => {
    setActionType(action);
    setOpenConfirmDialog(true);
  };

  const handleConfirmAction = async () => {
    setOpenConfirmDialog(false);
    setLoading(true);
    setError(null);
    try {
      if (actionType === 'obsolete') {
        const response = await apiClient.makeRequest('/health-insights/test-cases/mark-obsolete', {
          method: 'POST',
          body: JSON.stringify({
            project_id: selectedProject?.id || selectedProject?.project_id,
            test_case_ids: Array.from(selectedIds),
          }),
        });

        if (response.success) {
          setResults((prev) =>
            prev.map((tc) =>
              selectedIds.has(tc.id) ? { ...tc, status: 'obsolete', updated: true } : tc
            )
          );
          setSelectedIds(new Set());
        } else {
          setError(response.message || 'Failed to mark test cases as obsolete.');
        }
      }
    } catch (err) {
      console.error('Bulk action error:', err);
      setError(err.message || `Failed to perform ${actionType} action.`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'obsolete': return 'error';
      case 'draft': return 'info';
      case 'review': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Test Cases Cleanup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Identify and manage stale or obsolete test cases based on current Product Knowledge Base content.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={handleAnalyzeTestCases}
            disabled={analyzing}
            startIcon={analyzing ? <CircularProgress size={20} /> : <SearchIcon />}
          >
            {analyzing ? 'Analyzing...' : 'Analyze All Test Cases'}
          </Button>

          {results.length > 0 && selectedIds.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleBulkAction('obsolete')}
              disabled={loading || analyzing}
              startIcon={<DeleteIcon />}
            >
              Mark Selected as Obsolete ({selectedIds.size})
            </Button>
          )}
        </Box>
        {analyzing && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Analyzing {results.length} test cases...
            </Typography>
          </Box>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {!analyzing && results.length > 0 && (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.size > 0 && selectedIds.size < results.length}
                    checked={results.length > 0 && selectedIds.size === results.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>Test ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Match</TableCell>
                <TableCell>Current Status</TableCell>
                <TableCell>Suggested Status</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Created Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((testCase) => {
                const isItemSelected = isSelected(testCase.id);
                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, testCase.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={testCase.id}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isItemSelected} />
                    </TableCell>
                    <TableCell>{testCase.id}</TableCell>
                    <TableCell>{testCase.title}</TableCell>
                    <TableCell>{testCase.match}</TableCell>
                    <TableCell>
                      <Chip
                        label={testCase.status}
                        color={getStatusChipColor(testCase.status)}
                        size="small"
                      />
                      <FormControl variant="standard" sx={{ minWidth: 120, ml: 1 }} size="small">
                        <Select
                          value={testCase.status}
                          onChange={(event) => handleStatusChange(event, testCase.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={loading}
                        >
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="obsolete">Obsolete</MenuItem>
                          <MenuItem value="draft">Draft</MenuItem>
                          <MenuItem value="review">Review</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={testCase.suggestedStatus}
                        color={getStatusChipColor(testCase.suggestedStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{testCase.lastUpdated ? format(new Date(testCase.lastUpdated), 'yyyy-MM-dd') : 'N/A'}</TableCell>
                    <TableCell>{testCase.createdDate ? format(new Date(testCase.createdDate), 'yyyy-MM-dd') : 'N/A'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!analyzing && !error && results.length === 0 && (
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
          No test cases found, or analysis not yet run.
        </Typography>
      )}

      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to {actionType === 'obsolete' ? 'mark' : 'update'} {selectedIds.size} selected test case(s)?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmAction} autoFocus color="primary" disabled={loading}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestCasesCleanup;

